import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import Styled from 'styled-components';
import {
	Alert,
	Detail,
	FlexColumn,
	FlexRow,
	Icon,
	Modal,
	NestedCard,
	SubHeading,
	Text,
	tokensForThemeProvider,
} from '@forecast-it/design-system';
import Spinner, {SpinnerStyle} from '../../../forecast-app/shared/components/spinner/Spinner';
import {useForecastFetchQuery} from '../../../forecast-app/shared/hooks/useForecastFetchQuery';
import {
	DASH,
	formatDuration,
	formatHours,
	formatUpdatedAt,
} from '../../../forecast-app/admin-tab/templates-page/baseline/baselineTemplateFormat';
import {hasLines, insertedDeliverableCount, phaseLevelLinesGetPhaseDeliverable, previewNotes} from './insertTemplateFormat';
import type {InsertTemplatePreviewQuery} from './__generated__/InsertTemplatePreviewQuery.graphql';

// The template's totals sit together on a light grey panel, labels small and grey above their values.
const StatsPanel = Styled.div`
	background: ${tokensForThemeProvider.sky.ref.color.neutral['200']};
	border-radius: 4px;
	padding: 12px 16px;
`;

// The phases scroll on their own, so the totals above and the note below stay in view.
const PhaseList = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	max-height: 340px;
	overflow-y: auto;
`;

// A phase's deliverable sits on a light blue bar at the top of its white body.
const DeliverableBar = Styled.div`
	background: ${tokensForThemeProvider.sky.ref.color.blue['200']};
	border-radius: 4px;
	padding: 6px 12px;
`;

const RolesAndExpenses = Styled.div`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
`;

const ON_DARK = tokensForThemeProvider.sky.ref.color.neutral['50'];

// The phase header, in place of NestedCard.Header: that one always keeps a section label beside the
// name and room for a menu and a chevron at its end, so the name would sit further from the icon and
// the week and hours could not reach the right edge. Padding and colours are NestedCard's own.
const PhaseHeader = Styled.div`
	display: flex;
	align-items: center;
	gap: ${tokensForThemeProvider.sky.sys.spacing['100']};
	padding: ${tokensForThemeProvider.sky.sys.spacing['300']};

	* {
		color: ${ON_DARK};
	}
`;

const PhaseHeaderEnd = Styled.div`
	display: flex;
	align-items: center;
	gap: ${tokensForThemeProvider.sky.sys.spacing['300']};
	margin-left: auto;
`;

// One line of the note's text is 20px tall, so the icon is centred on the first line, not the top edge.
const NoteIconSlot = Styled.div`
	display: flex;
	align-items: center;
	flex: none;
	height: 20px;
`;

const query = graphql`
	query InsertTemplatePreviewQuery($projectId: ID, $baselineTemplateId: ID!) {
		viewer {
			project(internalId: $projectId) {
				baselineTemplatePreview(baselineTemplateId: $baselineTemplateId) {
					name
					description
					includeDeliverables
					usageCount
					updatedAt
					createdBy {
						fullName
					}
					totalMinutes
					roleCount
					deliverableCount
					durationDays
					phases {
						name
						startWeek
						durationDays
						totalMinutes
						expenseCount
						timeEstimates {
							roleName
							minutes
						}
						deliverables {
							name
							totalMinutes
							expenseCount
							timeEstimates {
								roleName
								minutes
							}
						}
						noDeliverable {
							name
							totalMinutes
							expenseCount
							timeEstimates {
								roleName
								minutes
							}
						}
					}
				}
			}
		}
	}
`;

const isPresent = <T,>(value: T | null): value is T => value !== null;

type Lines = {
	readonly timeEstimates: ReadonlyArray<{readonly roleName: string | null; readonly minutes: number | null} | null> | null;
	readonly expenseCount: number | null;
};

/** One line of role chips and an expense count, as the preview shows a phase or one of its deliverables. */
const GroupLines = ({lines}: {lines: Lines}): JSX.Element | null => {
	const intl = useIntl();
	const roleLine = (lines.timeEstimates ?? [])
		.filter(isPresent)
		.map(line => `${line.roleName} ${formatHours(intl, line.minutes ?? 0)}`)
		.join(' · ');
	if (!roleLine && !lines.expenseCount) {
		return null;
	}
	return (
		<RolesAndExpenses>
			<Detail color="auxiliary">{roleLine}</Detail>
			{lines.expenseCount ? (
				<Detail color="auxiliary">
					{intl.formatMessage({id: 'baseline_templates.insert.preview.expenses'}, {count: lines.expenseCount})}
				</Detail>
			) : null}
		</RolesAndExpenses>
	);
};

type Props = {
	projectId: string;
	templateId: string;
	projectUsesDeliverables: boolean;
	onBack: () => void;
	onInsert: (template: {id: string; name: string | null}) => void;
	onCancel: () => void;
};

/**
 * Whether a failed preview fetch is the server saying the template is gone (deleted or deactivated
 * since the list loaded). The graph passes that refusal through as `BASELINE_TEMPLATE_NOT_FOUND: <id>`
 * in the error message; anything else — a network failure, an expired login — is not a missing template.
 */
const isTemplateNotFound = (error: unknown): boolean =>
	error instanceof Error && error.message.includes('BASELINE_TEMPLATE_NOT_FOUND');

type Failure = 'not_found' | 'other';

export const InsertTemplatePreview = ({
	projectId,
	templateId,
	projectUsesDeliverables,
	onBack,
	onInsert,
	onCancel,
}: Props): JSX.Element => {
	const intl = useIntl();
	const {data, fetch, loading} = useForecastFetchQuery<
		InsertTemplatePreviewQuery['response'],
		{projectId: string; baselineTemplateId: string}
	>(query, true);

	const [failure, setFailure] = useState<Failure | null>(null);

	useEffect(() => {
		setFailure(null);
		fetch({projectId, baselineTemplateId: templateId}, undefined, error =>
			setFailure(isTemplateNotFound(error) ? 'not_found' : 'other')
		);
	}, [projectId, templateId]);

	const preview = data?.viewer?.project?.baselineTemplatePreview;
	const waiting = !failure && (loading || data === null);
	const phases = (preview?.phases ?? []).filter(isPresent);
	// Insert puts phase-level lines under a deliverable named after their phase when no line would sit under one.
	const phaseLevelNamedAfterPhase =
		projectUsesDeliverables && !!preview && phaseLevelLinesGetPhaseDeliverable({...preview, phases});
	// A preview that resolves to null is a template this viewer can no longer insert, which reads the
	// same as one that is gone; only a failure that is neither gets the generic message.
	const errorMessageId = failure === 'other' ? 'error_boundary.message1' : 'baseline_templates.insert.errors.not_found';

	const stat = (labelId: string, value: string, cy: string) => (
		<FlexColumn gap="xxs">
			<Detail color="auxiliary">{intl.formatMessage({id: labelId})}</Detail>
			<Text size="2" data-cy={cy}>
				{value}
			</Text>
		</FlexColumn>
	);

	return (
		<Modal size={Modal.SIZE.LARGE} onCloseModal={onCancel}>
			<Modal.Title>{preview?.name ?? intl.formatMessage({id: 'baseline_templates.insert.title'})}</Modal.Title>
			<Modal.Content>
				{waiting ? (
					<div
						role="status"
						aria-label={intl.formatMessage({id: 'common.loading'})}
						data-cy="insert-baseline-template-preview-loading"
					>
						<Spinner spinning style={SpinnerStyle.onLight} />
					</div>
				) : !preview ? (
					<div data-cy="insert-baseline-template-preview-error">
						<Alert type={Alert.TYPE.ERROR} text={intl.formatMessage({id: errorMessageId})} />
					</div>
				) : (
					<FlexColumn gap="l">
						<FlexColumn gap="xxs">
							{preview.description ? (
								<Text color="auxiliary" size="2">
									{preview.description}
								</Text>
							) : null}
							<Detail color="auxiliary">
								{intl.formatMessage(
									{id: 'baseline_templates.insert.preview.meta'},
									{
										name: preview.createdBy?.fullName ?? DASH,
										date: formatUpdatedAt(preview.updatedAt),
										count: preview.usageCount ?? 0,
									}
								)}
							</Detail>
						</FlexColumn>
						<StatsPanel>
							<FlexRow gap="xl" wrap="wrap">
								{stat(
									'baseline_templates.insert.preview.work_estimate',
									formatHours(intl, preview.totalMinutes ?? 0),
									'insert-baseline-template-stat-hours'
								)}
								{stat(
									'baseline_templates.insert.preview.roles',
									String(preview.roleCount ?? 0),
									'insert-baseline-template-stat-roles'
								)}
								{stat(
									'baseline_templates.insert.preview.duration',
									formatDuration(intl, preview.durationDays ?? 0),
									'insert-baseline-template-stat-duration'
								)}
								{stat(
									'baseline_templates.insert.preview.deliverables',
									projectUsesDeliverables ? String(insertedDeliverableCount({...preview, phases})) : DASH,
									'insert-baseline-template-stat-deliverables'
								)}
							</FlexRow>
						</StatsPanel>
						<PhaseList>
							{phases.map((phase, index) => {
								const groups = projectUsesDeliverables ? (phase.deliverables ?? []).filter(isPresent) : [];
								const noDeliverable = groups.length > 0 ? phase.noDeliverable : null;
								const hasBody = groups.length > 0 || hasLines(phase);
								return (
									<NestedCard
										key={index}
										colorTheme="dark"
										dataCy={`insert-baseline-template-phase-${index}`}
									>
										<PhaseHeader>
											<Icon icon="phase" size="l" customColor={ON_DARK} />
											<SubHeading>{phase.name}</SubHeading>
											<PhaseHeaderEnd>
												<Text>
													{intl.formatMessage(
														{id: 'baseline_templates.insert.preview.phase_timing'},
														{week: phase.startWeek, days: phase.durationDays}
													)}
												</Text>
												<SubHeading>{formatHours(intl, phase.totalMinutes ?? 0)}</SubHeading>
											</PhaseHeaderEnd>
										</PhaseHeader>
										{hasBody ? (
											// The body goes in the card's content box, which is white: only the header is navy.
											<NestedCard.UncollapsibleContentWrapper>
												<NestedCard.Content>
													{groups.length > 0 ? (
														<FlexColumn gap="s">
															{groups.map((group, groupIndex) => (
																<FlexColumn gap="xs" key={groupIndex}>
																	<DeliverableBar>
																		<Detail>{group.name}</Detail>
																	</DeliverableBar>
																	<GroupLines lines={group} />
																</FlexColumn>
															))}
															{noDeliverable ? (
																<FlexColumn gap="xs">
																	<DeliverableBar>
																		<Detail>
																			{phaseLevelNamedAfterPhase && phase.name
																				? phase.name
																				: intl.formatMessage({
																						id: 'deliverable.no_deliverable',
																				  })}
																		</Detail>
																	</DeliverableBar>
																	<GroupLines lines={noDeliverable} />
																</FlexColumn>
															) : null}
														</FlexColumn>
													) : (
														<GroupLines lines={phase} />
													)}
												</NestedCard.Content>
											</NestedCard.UncollapsibleContentWrapper>
										) : null}
									</NestedCard>
								);
							})}
						</PhaseList>
						<FlexRow gap="s" alignItems="flex-start" data-cy="insert-baseline-template-preview-note">
							<NoteIconSlot>
								<Icon icon="info" size="s" />
							</NoteIconSlot>
							<Text color="auxiliary" size="2">
								{previewNotes(intl, {...preview, phases}, projectUsesDeliverables).join(' ')}
							</Text>
						</FlexRow>
					</FlexColumn>
				)}
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={() => onInsert({id: templateId, name: preview?.name ?? null})}
					disabled={!preview}
					dataCy="insert-baseline-template-preview-insert"
				>
					{intl.formatMessage({id: 'baseline_templates.insert.preview.insert_template'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={onBack} dataCy="insert-baseline-template-back">
					{intl.formatMessage({id: 'baseline_templates.insert.preview.back'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};
